import { Transaction } from 'dexie';
import {
  fetchBookChaptersV2,
  syncedDBModel,
  syncStateManager,
} from '@taleemabad/db';
import { SCHEMAS } from './lessonplan';

export interface DBMigration {
  version: number;
  schema: Record<string, string>;
  upgrade?: (tx: Transaction) => Promise<void>;
}

/**
 * IMPORTANT MIGRATION RULES:
 * - **DO NOT REMOVE previous versions** because some users may still be running older versions.
 * - **DO NOT MODIFY schemas of previous versions** to ensure backward compatibility.
 * - **ONLY ADD a new version if there is an upgrade function.**
 *   - If there's no upgrade logic required, just update the latest version number and schema.
 *
 * For more information: Read this document -> https://docs.google.com/document/d/13SNDOIBcaFAoQTqBG8jKTeaQ121pQ9zYDJHE7iTs8d0
 */
export const migrations: DBMigration[] = [
  { version: 2, schema: SCHEMAS.v1 },
  {
    version: 3,
    schema: SCHEMAS.v2,
    upgrade: async (tx) => {
      console.log('Upgrading schema to version 3...');
      await clearAndFetchLessonPlans(tx);
    },
  },
  { version: 4, schema: SCHEMAS.v3 },
  { version: 8, schema: SCHEMAS.v5 },
  {
    version: 6,
    schema: SCHEMAS.v3,
    upgrade: async (tx) => {
      console.log('Migration: Removing LessonPlan DB');
      await clearLessonPlanDB(tx);
    },
  },
  {
    version: 7,
    schema: SCHEMAS.v4,
    upgrade: async (tx) => {
      console.log('Migration: Upgrading to version 7...');
      await refetchChanges(tx);
    },
  },
  // v9: No schema change — version bump ensures Dexie's upgrade path runs for
  // existing users after the `deletionBlocked` field was added to
  // LessonPlanTimetableRecord (schoolClassTimetables table).
  { version: 9, schema: SCHEMAS.v5 },
];

const refetchChanges = async (tx: Transaction) => {
  console.log('Refetching changes due to schema upgrade...');
  await syncedDBModel.resetSyncRecord();
  await syncStateManager.checkChanges();
};

const clearAndFetchLessonPlans = async (tx: Transaction) => {
  console.log('Clearing lessonPlanDB due to schema upgrade...');
  await tx.table('lessonPlans').clear();
  console.log('Database cleared. Fetching fresh data...');

  try {
    await fetchBookChaptersV2(null);
    console.log('Fresh data fetched successfully.');
  } catch (error) {
    syncedDBModel.resetSyncRecord();
    console.error('Failed to fetch data:', error);
  }
};

const clearLessonPlanDB = async (tx: Transaction) => {
  const deleteTables = [
    'lessonPlans',
    'chapters',
    'gradeSubjects',
    'schoolClasses',
    'schoolClassSubjects',
    'grades',
    'subjects',
    'schoolClassTimetables',
  ];

  try {
    const classes = await tx.table('schoolClasses').toArray();
    const newSessionClassExist = classes.some((item) => item?.session === 2);
    if (newSessionClassExist) {
      return;
    }
    for (const tableName of deleteTables) {
      console.log(`Clearing table: ${tableName}`);
      await tx.table(tableName).clear();
    }

    tx.on('complete', () => {
      fetchBookChaptersV2(null);
    });
  } catch (error) {
    console.error(
      'Failed during LessonPlanDB clearing or data fetching:',
      error,
    );
    syncedDBModel.resetSyncRecord();
  }
};
